"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validate6 = exports.validate4 = exports.validate = exports.isValid6 = exports.isValid4 = exports.isValid = exports.intToIp = exports.ipToInt = exports.ipv6Unpack = exports.ipv6Pack = exports.binToDec = exports.getType = void 0;
/*!
 * Copyright (c) 2018, imqueue.com <support@imqueue.com>
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
 * REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
 * AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
 * INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
 * LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
 * OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
 * PERFORMANCE OF THIS SOFTWARE.
 */
const ip_regex_1 = __importDefault(require("ip-regex"));
const types_1 = require("./types");
function invalid(ip) {
    return `Given network address "${ip}" is invalid!`;
}
/**
 * Ensures type is set for given address
 *
 * @param {string} ip
 * @param {NetworkType} [type]
 * @return {NetworkType}
 */
function getType(ip, type) {
    if (!ip) {
        throw new TypeError(invalid(ip));
    }
    if (typeof type === 'undefined') {
        if (~ip.indexOf(':')) {
            type = types_1.NetworkType.IPV6;
        }
        else if (~ip.indexOf('.')) {
            type = types_1.NetworkType.IPV4;
        }
        else {
            throw new TypeError(invalid(ip));
        }
    }
    else if (!Object.values(types_1.NetworkType).includes(type)) {
        throw new TypeError(`Given network type ${type} is invalid!`);
    }
    else if (getType(ip) !== type) {
        throw new TypeError(`Looks like given type "${type}" does not match actual type "${getType(ip)}"!`);
    }
    return type;
}
exports.getType = getType;
/**
 * Converts binary integer representation string to big integer
 *
 * @param {string} binStr
 * @return {bigint}
 */
function binToDec(binStr) {
    const lastIndex = binStr.length - 1;
    let total = BigInt(0);
    for (let i = 0; i < binStr.length; i++) {
        if (binStr[lastIndex - i] === '1') {
            total += (BigInt(2) ** BigInt(i));
        }
    }
    return total;
}
exports.binToDec = binToDec;
const RX_IPV6_OCTET_PACK = /^0+/;
const RX_IPV6_PACK = /\b:?(?:0+:?){2,}/;
const RX_IPV6_TUNNELS_CLEAN = /\./g;
/**
 * Packs and returns most possible compact IPv6 address representation
 *
 * @param {string} ip
 * @return {string}
 */
function ipv6Pack(ip) {
    return ip.split(':')
        .map(part => {
        let clean = part.replace(RX_IPV6_OCTET_PACK, '');
        clean === '' && (clean = '0');
        return clean;
    })
        .join(':')
        .replace(RX_IPV6_PACK, '::');
}
exports.ipv6Pack = ipv6Pack;
/**
 * Returns full canonical representation of IPv6 address
 *
 * @param {string} ip
 * @return {string}
 */
function ipv6Unpack(ip) {
    if (ip.length === types_1.IPV6_MAX_STR_LEN) {
        return ip;
    }
    // make sure tunneling ip format will not cause a problem
    ip = ip.replace(RX_IPV6_TUNNELS_CLEAN, ':');
    const chunks = ip.split('::');
    let parts = [];
    if (chunks.length === 2) {
        const startParts = chunks[0].split(':');
        const endParts = chunks[1].split(':');
        parts = parts.concat(startParts);
        for (let i = 0; i < 8 - startParts.length - endParts.length; i++) {
            parts.push('0000');
        }
        parts = parts.concat(chunks[1] === '' ? ['0000'] : endParts);
    }
    else if (chunks.length === 1) {
        parts = ip.split(':');
    }
    else {
        throw new TypeError(invalid(ip));
    }
    return parts.map(part => part.length < 4 ? '0'.repeat(4 - part.length) + part : part).join(':');
}
exports.ipv6Unpack = ipv6Unpack;
/**
 * Converts given address to big integer representation
 *
 * @param {string} ip
 * @param {NetworkType} [type]
 * @return {bigint}
 */
function ipToInt(ip, type) {
    validate(ip);
    type = getType(ip, type);
    // IPv4
    if (type === types_1.NetworkType.IPV4) {
        const parts = ip.split('.');
        let total = 0;
        for (let i = 0; i < parts.length; i++) {
            total += parseInt(parts[i]) *
                Math.pow(256, (parts.length - i - 1));
        }
        return BigInt(total);
    }
    // IPv6
    const octets = ipv6Unpack(ip).split(':');
    const parts = [];
    for (let i = 0; i < octets.length; i++) {
        let bin = parseInt(octets[i], 16).toString(2);
        if (bin.length < 16) {
            bin = '0'.repeat(16 - bin.length) + bin;
        }
        parts.push(bin);
    }
    return binToDec(parts.join(''));
}
exports.ipToInt = ipToInt;
/**
 * Converts big integer address representation to it's string canonical form
 *
 * @param {bigint} intIp
 * @param {NetworkType} type
 * @param {boolean} [canonical]
 * @return {string}
 */
function intToIp(intIp, type, canonical = false) {
    if (type === types_1.NetworkType.IPV6) {
        return (canonical ? ipv6Unpack : ipv6Pack)((intIp >> BigInt(112)).toString(16) + ':' +
            (intIp >> BigInt(96) & BigInt(0xFFFF)).toString(16) + ':' +
            (intIp >> BigInt(80) & BigInt(0xFFFF)).toString(16) + ':' +
            (intIp >> BigInt(64) & BigInt(0xFFFF)).toString(16) + ':' +
            (intIp >> BigInt(48) & BigInt(0xFFFF)).toString(16) + ':' +
            (intIp >> BigInt(32) & BigInt(0xFFFF)).toString(16) + ':' +
            (intIp >> BigInt(16) & BigInt(0xFFFF)).toString(16) + ':' +
            (intIp & BigInt(0xFFFF)).toString(16));
    }
    else {
        return (intIp >> BigInt(24)) + '.' +
            (intIp >> BigInt(16) & BigInt(0xFF)) + '.' +
            (intIp >> BigInt(8) & BigInt(0xFF)) + '.' +
            (intIp & BigInt(0xFF));
    }
}
exports.intToIp = intToIp;
// istanbul ignore next
/**
 * Returns true if given IP valid network address (IPv4 or IPv6), false -
 * otherwise
 *
 * @param {string} ip
 * @return {boolean}
 */
function isValid(ip) {
    return (0, ip_regex_1.default)({ exact: true }).test(ip);
}
exports.isValid = isValid;
// istanbul ignore next
/**
 * Returns true if given IP is a valid IPv4 network address, false - otherwise
 *
 * @param {string} ip
 * @return {boolean}
 */
function isValid4(ip) {
    return ip_regex_1.default.v4({ exact: true }).test(ip);
}
exports.isValid4 = isValid4;
// istanbul ignore next
/**
 * Returns true if given IP is a valid IPv6 network address, false - otherwise
 *
 * @param {string} ip
 * @return {boolean}
 */
function isValid6(ip) {
    return ip_regex_1.default.v6({ exact: true }).test(ip);
}
exports.isValid6 = isValid6;
// istanbul ignore next
/**
 * If given address is not valid address (v4 or v6) - throws an error
 *
 * @param {string} ip
 */
function validate(ip) {
    if (!isValid(ip)) {
        throw new TypeError(invalid(ip));
    }
}
exports.validate = validate;
// istanbul ignore next
/**
 * If given address is not valid IPv4 address - throws an error
 *
 * @param {string} ip
 */
function validate4(ip) {
    if (!isValid4(ip)) {
        throw new TypeError(invalid(ip));
    }
}
exports.validate4 = validate4;
// istanbul ignore next
/**
 * If given address is not valid IPv6 address - throws an error
 *
 * @param {string} ip
 */
function validate6(ip) {
    if (!isValid6(ip)) {
        throw new TypeError(invalid(ip));
    }
}
exports.validate6 = validate6;
//# sourceMappingURL=ip-address.js.map